import { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Calendar } from "lucide-react";
import api, { getEventPL } from "@/lib/api";
import { PLSummaryCards } from "@/components/revenue/PLSummaryCards";
import { ProfitMarginsWidget } from "@/components/revenue/ProfitMarginsWidget";
import { MonthlyPLChart, RevenueTrendChart } from "@/components/revenue/RevenueCharts";
import { ClientRevenueSection } from "@/components/revenue/ClientRevenueSection";
import { ExpensesSection } from "@/components/revenue/ExpensesSection";
import { EventPLTable } from "@/components/revenue/EventPLTable";
import { GearRevenueSection } from "@/components/revenue/GearRevenueSection";

const RANGE_OPTIONS = [
  { value: "12m", label: "Last 12 Months" },
  { value: "3m", label: "Last 3 Months" },
  { value: "1m", label: "Last Month" },
  { value: "ytd", label: "Year to Date" },
  { value: "custom", label: "Custom Range" },
];

export default function RevenuePage() {
  const { role } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("12m");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [eventPL, setEventPL] = useState(null);
  const [eventPLOpen, setEventPLOpen] = useState(false);
  const [clientRevOpen, setClientRevOpen] = useState(false);

  const dateParams = useMemo(() => {
    const now = new Date();
    let start = null;
    let end = now.toISOString().split("T")[0];
    if (range === "12m") { const d = new Date(now); d.setMonth(d.getMonth() - 12); start = d.toISOString().split("T")[0]; }
    else if (range === "3m") { const d = new Date(now); d.setMonth(d.getMonth() - 3); start = d.toISOString().split("T")[0]; }
    else if (range === "1m") { const d = new Date(now); d.setMonth(d.getMonth() - 1); start = d.toISOString().split("T")[0]; }
    else if (range === "ytd") { start = `${now.getFullYear()}-01-01`; }
    else if (range === "custom") { start = customStart || null; end = customEnd || null; }
    return { start, end };
  }, [range, customStart, customEnd]);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateParams.start) params.start_date = dateParams.start;
      if (dateParams.end) params.end_date = dateParams.end;
      const [revRes, plRes] = await Promise.all([api.get("/dashboard/revenue", { params }), getEventPL(params)]);
      setData(revRes.data);
      setEventPL(plRes.data);
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Revenue data is restricted to owners" : "Failed to load revenue data");
    } finally { setLoading(false); }
  }, [dateParams.start, dateParams.end]);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  if (role !== "owner") {
    return <div data-testid="revenue-page" className="flex items-center justify-center h-64"><p className="text-muted-foreground">Revenue data is only available to owners.</p></div>;
  }
  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const totalRevenue = data?.total_revenue || 0;
  const totalExpenses = data?.total_expenses || 0;
  const monthlyPL = data?.monthly_pl || [];
  const monthlyRevData = data?.monthly_revenue || [];

  return (
    <div data-testid="revenue-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Revenue & P&L</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {data?.has_invoices ? "Invoice-based financials" : "Estimate-based revenue"} &middot; {RANGE_OPTIONS.find(r => r.value === range)?.label}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="revenue-range-select" className="w-44 h-9 text-sm bg-secondary/30 border-border"><Calendar className="w-3.5 h-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          {range === "custom" && (
            <div className="flex items-center gap-2">
              <Input data-testid="revenue-custom-start" type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-36 h-9 text-sm bg-secondary/30 border-border" />
              <span className="text-muted-foreground text-xs">to</span>
              <Input data-testid="revenue-custom-end" type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-36 h-9 text-sm bg-secondary/30 border-border" />
            </div>
          )}
        </div>
      </div>

      <PLSummaryCards data={data} />
      <ProfitMarginsWidget summary={eventPL?.summary} />
      <MonthlyPLChart monthlyPL={monthlyPL} />
      {!monthlyPL.length && <RevenueTrendChart monthlyRevData={monthlyRevData} />}
      <ClientRevenueSection clientData={data?.client_revenue || []} totalRevenue={totalRevenue} hasInvoices={data?.has_invoices} open={clientRevOpen} onToggle={() => setClientRevOpen(p => !p)} />
      <ExpensesSection expByCategory={data?.expense_by_category || []} expByVendor={data?.expense_by_vendor || []} totalExpenses={totalExpenses} />
      <EventPLTable events={eventPL?.events} open={eventPLOpen} onToggle={() => setEventPLOpen(p => !p)} />
      <GearRevenueSection gearData={data?.gear_revenue || []} totalRevenue={totalRevenue} />
    </div>
  );
}
